import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  CalendarDays, CalendarRange, ChevronLeft, Copy, FolderOpen, Loader2, Megaphone, MessageSquare,
  SlidersHorizontal, Users, Vote, Wallet,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Chip, Err, Panel } from "@/components/community/kit";
import { ROLE_TONE, allows, communityBase } from "@/lib/community";
import { Chat } from "@/components/community/Chat";
import { News } from "@/components/community/News";
import { Meetings } from "@/components/community/Meetings";
import { Members } from "@/components/community/Members";
import { Docs } from "@/components/community/Docs";
import { Votes } from "@/components/community/Votes";
import { Dues } from "@/components/community/Dues";
import { Rules } from "@/components/community/Rules";
import { Calendar } from "@/components/community/Calendar";

const TABS = [
  { key: "chat", label: "Chat", icon: MessageSquare },
  { key: "news", label: "News", icon: Megaphone },
  { key: "calendar", label: "Calendar", icon: CalendarRange },
  { key: "votes", label: "Votes", icon: Vote },
  { key: "meetings", label: "Meetings", icon: CalendarDays },
  { key: "members", label: "Members", icon: Users },
  { key: "dues", label: "Dues", icon: Wallet },
  { key: "docs", label: "Documents", icon: FolderOpen },
  { key: "rules", label: "Settings", icon: SlidersHorizontal },
];

export default function CommunityRoom() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "chat";
  const [community, setCommunity] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/communities/${id}`);
      setCommunity(data.community);
    } catch (e) { setError(errMsg(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: true });
  };

  if (error && !community) {
    return (
      <div data-testid="community-room" className="space-y-4">
        <Err testId="community-room-error">{error}</Err>
        <Link to={communityBase()} data-testid="community-room-back"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-gold">
          <ChevronLeft className="h-4 w-4" /> All communities
        </Link>
      </div>
    );
  }

  if (!community) {
    return (
      <div data-testid="community-room-loading" className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }

  const pending = community.my_status === "pending";
  const visible = TABS.filter((t) =>
    (t.key !== "dues" || allows(community, "money")) && (t.key !== "rules" || true));

  return (
    <div data-testid="community-room" className="space-y-5">
      <Link to={communityBase()} data-testid="community-room-back"
        className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-500 transition-colors hover:text-gold">
        <ChevronLeft className="h-4 w-4" /> All communities
      </Link>

      <div className="rounded-2xl border border-ink-600 bg-gradient-to-br from-ink-800 to-ink-900 p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gold">
              {community.type_label}
            </p>
            <h1 data-testid="community-name" className="mt-1.5 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              {community.name}
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              {[community.city, community.state].filter(Boolean).join(", ")}
              {community.counts?.members ? ` · ${community.counts.members} members` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Chip testId="community-my-role" className={ROLE_TONE[community.my_role] || ROLE_TONE.member}>
              {community.platform_admin ? "Platform owner" : community.my_role_label || "Member"}
            </Chip>
            {community.code && (
              <button data-testid="community-copy-code"
                onClick={() => {
                  navigator.clipboard?.writeText(community.code);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1800);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-ink-600 bg-ink-900 px-3 py-1 font-mono text-[11px] tracking-[0.2em] text-slate-300 transition-colors hover:border-gold/40 hover:text-gold">
                <Copy className="h-3 w-3" /> {copied ? "Copied" : community.code}
              </button>
            )}
          </div>
        </div>
      </div>

      {pending ? (
        <Panel testId="community-pending" title="Waiting to be let in">
          <p className="text-sm text-slate-400">
            Your request to join {community.name} is with its leaders. You'll get a notification the
            moment it's approved, and the chat, meetings and votes open up then.
          </p>
        </Panel>
      ) : (
        <>
          <div data-testid="community-tabs" className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {visible.map((t) => (
              <button key={t.key} data-testid={`community-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                  tab === t.key ? "border-gold/40 bg-gold/10 text-gold"
                    : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "chat" && <Chat community={community} />}
          {tab === "news" && <News community={community} />}
          {tab === "calendar" && <Calendar community={community} onTab={setTab} />}
          {tab === "votes" && <Votes community={community} />}
          {tab === "meetings" && <Meetings community={community} />}
          {tab === "members" && <Members community={community} onChanged={load} />}
          {tab === "dues" && <Dues community={community} />}
          {tab === "docs" && <Docs community={community} />}
          {tab === "rules" && <Rules community={community} onChanged={load} />}
        </>
      )}
    </div>
  );
}
